"use client";

import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { addDays, format } from "date-fns";
import type { DateRange } from "react-day-picker";

import { cn } from "@lib/utils";
import { Button } from "@components/ui/button";
import { Calendar } from "@components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";

interface DatePickerWithRangeProps {
  defaultRange: DateRange | undefined;
  className?: string;
  disabled?: boolean;
  onDateChange?: (dateRange: DateRange | undefined) => void;
}

export function DatePickerWithRange({
  defaultRange,
  className,
  disabled = false,
  onDateChange,
}: DatePickerWithRangeProps) {
  const [appliedRange, setAppliedRange] = React.useState<DateRange | undefined>(
    defaultRange
  );

  const [selectedRange, setSelectedRange] = React.useState<
    DateRange | undefined
  >(appliedRange);

  const [open, setOpen] = React.useState(false);

  const handleApply = () => {
    setAppliedRange(selectedRange);
    setOpen(false);
    onDateChange?.(selectedRange);
  };

  React.useEffect(() => {
    if (open) {
      setSelectedRange(appliedRange);
    }
  }, [open, appliedRange]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            disabled={disabled}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !appliedRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {appliedRange?.from ? (
              appliedRange.to ? (
                <>
                  {format(appliedRange.from, "LLL dd, y")} -{" "}
                  {format(appliedRange.to, "LLL dd, y")}
                </>
              ) : (
                format(appliedRange.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={selectedRange?.from}
            selected={selectedRange}
            onSelect={setSelectedRange}
            numberOfMonths={2}
            disabled={{
              after: new Date(),
            }}
          />
          <div className="flex items-center justify-end gap-2 p-3 border-t">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply Range
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
